import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'

import { basicKeybindSkips } from 'components/deckPage/modals/KeybindsOverlay'

import BackButton from 'components/elements/Overlay/BackButton'

import styles from './overlay.module.scss'

export type OverlayProps = {
  open?: boolean
  className?: string
  openClassName?: string
  style?: React.CSSProperties
  children?: React.ReactNode
  onClose?: () => void
  fadeTime?: number
  ignoreBehindScrolling?: boolean
}

const Overlay = ({
  open,
  className = '',
  openClassName = '',
  style,
  children = null,
  onClose,
  fadeTime = 200,
  ignoreBehindScrolling = false,
}: OverlayProps) => {
  const openRef = useRef(open)

  const [visible, setVisible] = useState(open)
  const [closeClickStarted, setCloseClickStarted] = useState(false)

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!onClose) return
    if (openRef.current === false) return

    if (basicKeybindSkips(e)) return

    if (e.key === 'Escape') {
      e.preventDefault() // prevents exiting fullscreen on macos (I think)

      onClose()
    }
  }

  // Using the built in onClick means that if you start the click in the modal and end it in the dimmer area it closes
  // the modal. This is frustrating when selecting text in an input field. To avoid this we track if the click started
  // in the dimmer area and only close the modal if it did and it also ended in the dimmer area.
  const handleWeirdCloseMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) setCloseClickStarted(true)
    else setCloseClickStarted(false)
  }
  const handleWeirdCloseMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && closeClickStarted) onClose && onClose()
    setCloseClickStarted(false)
  }

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)

    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  useEffect(() => {
    if (open && !ignoreBehindScrolling) document.body.classList.add('no-scrolling')
    else document.body.classList.remove('no-scrolling')

    openRef.current = open

    if (fadeTime) setTimeout(() => setVisible(open), fadeTime)
    else setVisible(open)

    return () => document.body.classList.remove('no-scrolling')
  }, [open, ignoreBehindScrolling, fadeTime])

  // THIS IS INTENTIONAL
  // If open is undefined then we still render the component, and assume that the parent will deal with hiding/ showing it's view
  // This is largely going to be used to keep the children in the DOM, and hide/show this component via CSS
  if (open === false && visible === false) return null

  return (
    <div
      className={`${visible && open && `${styles.open} ${openClassName}`} ${styles.overlay} ${className}`}
      style={{
        // prettier-ignore
        transition: `transform ${fadeTime}ms ease-out, opacity ${fadeTime / 2}ms ease-out, background-color ${fadeTime / 1.5}ms ease-out`,
        ...style,
      }}>
      {children}
      <div onMouseDown={handleWeirdCloseMouseDown} onMouseUp={handleWeirdCloseMouseUp} className={styles.weirdCloseContainer} />
      <div className={styles.grid} />
    </div>
  )
}

export default Overlay

export const PageOverlay = (props: OverlayProps) => {
  if (typeof document === 'undefined') return null

  return ReactDOM.createPortal(<Overlay {...props} />, document.body)
}

type LayoutProps = {
  contentClassName?: string
}

export const CenteringOverlay = (props: OverlayProps & LayoutProps) => {
  const passedProps = { ...props }

  delete passedProps.children
  delete passedProps.contentClassName

  return (
    <PageOverlay {...props} className={`${styles.centerLayoutParentContainer} ${props.className || ''}`}>
      <div className={`${styles.centerLayoutContent} ${props.contentClassName}`}>{props.children}</div>
    </PageOverlay>
  )
}

export const OverlayExtras = {
  PageOverlay,
  CenteringOverlay,

  BackButton,
}
